/** The objectives panel (a campaign's objectives list): top left under the
 *  bar, each goal with a box ticked when done, the first not done marked
 *  as the current one. Its header folds it to "Objectives 3/9"; once every
 *  one is done it goes away for good. Nothing pops up and nothing points:
 *  it's there to be glanced at. */

import { For, Show, createEffect, type JSX } from "solid-js";
import { render } from "solid-js/web";
import { keepObjectives, objectives, objectivesKept, objectivesPlayer, runObjective } from "../state/objectives";

/** The objectives a click on puts a build on the cursor. */
const BUILDS = new Set(["iron_mine", "coal_mine", "steelworks", "rail", "barracks", "breach"]);

function ObjectivesPanel(): JSX.Element {
  const done = () => objectives().filter((o) => o.done).length;
  const current = () => objectives().find((o) => !o.done)?.id;
  const all = () => objectives().length > 0 && done() === objectives().length;
  // Every one done: remembered, so it stays away.
  createEffect(() => {
    if (all() && objectivesPlayer() && !objectivesKept().finished) keepObjectives({ finished: true });
  });
  const shown = () => objectives().length > 0 && !all() && !objectivesKept().finished;
  const collapsed = () => !!objectivesKept().collapsed;
  return (
    <Show when={shown()}>
      <section class="objectives" aria-label="Objectives">
        <button class="objectives-head" aria-expanded={!collapsed()} onClick={() => keepObjectives({ collapsed: !collapsed() })}>
          <span class="objectives-fold">{collapsed() ? "▸" : "▾"}</span>
          Objectives
          <span class="objectives-n">
            {done()}/{objectives().length}
          </span>
        </button>
        <Show when={!collapsed()}>
          <ol class="objectives-list">
            <For each={objectives()}>
              {(o) => {
                const now = () => o.id === current();
                const can = () => now() && BUILDS.has(o.id);
                return (
                  <li classList={{ done: o.done, current: now() }}>
                    <span class="objective-box" aria-label={o.done ? "Done" : "Not done"}>
                      {o.done ? "✓" : ""}
                    </span>
                    <Show when={can()} fallback={<span class="objective-text">{o.text}</span>}>
                      <button class="objective-text objective-go" onClick={() => runObjective(o.id)}>
                        {o.text}
                      </button>
                    </Show>
                  </li>
                );
              }}
            </For>
          </ol>
        </Show>
      </section>
    </Show>
  );
}

const host = document.createElement("div");
host.id = "objectives";
document.body.appendChild(host);
render(() => <ObjectivesPanel />, host);
